import { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Ban,
  CheckCircle,
  Trash2,
  UserCog,
  UserPlus,
  Mail,
  RefreshCw,
  Clock,
  Shield,
  type LucideIcon,
} from "lucide-react";
import {
  Card,
  List,
  ListItem,
  SearchField,
  Segmented,
  Button,
  Spinner,
  Badge,
  Dialog,
  ConfirmDialog,
  PageHeader,
  Select,
  Empty,
  useToast,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { adminApi } from "@/lib/adminApi";

interface User {
  id: string;
  name: string | null;
  email: string;
  subscriptionTier: "FREE" | "PRO";
  role: string;
  disabled: boolean;
  createdAt: string;
  _count?: { proposals: number };
  cooldownUntil?: string | null;
  cooldownReason?: string | null;
  cooldownNote?: string | null;
}

const TIER_VARIANT = {
  FREE: "neutral",
  PRO: "success",
} as const;

const TIER_LEADING_TINT = {
  FREE: "yellow",
  PRO: "pink",
} as const;

type TierFilter = "ALL" | "FREE" | "PRO";
const TIER_OPTIONS: TierFilter[] = ["ALL", "FREE", "PRO"];

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState<TierFilter>("ALL");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState("user");

  const { showToast } = useToast();

  const fetchUsers = async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.get<{
        success: boolean;
        users: User[];
        total: number;
        pages: number;
      }>(`/admin/users?page=${pageNum}&search=${encodeURIComponent(search)}&tier=${tier}`);
      if (res.success) {
        setUsers(res.users);
        setTotal(res.total);
        setTotalPages(res.pages);
        setPage(pageNum);
      }
    } catch (e: any) {
      setError(e.message || "Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => fetchUsers(1), 400);
    setSearchTimeout(timeout);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchUsers(newPage);
  };

  const handleUpgradeTier = async (userId: string, newTier: "FREE" | "PRO") => {
    try {
      await adminApi.put(`/admin/users/${userId}/tier`, { tier: newTier });
      showToast({
        title: "Tier updated",
        description: `User moved to ${newTier}`,
        variant: "success",
      });
      fetchUsers(page);
    } catch (e) {
      showToast({ title: "Failed to update tier", variant: "error" });
    }
  };

  const handleDisableUser = async (userId: string) => {
    setActionLoading(userId);
    try {
      await adminApi.put(`/admin/users/${userId}/disable`, {});
      showToast({ title: "User disabled", variant: "info" });
      fetchUsers(page);
    } catch (e) {
      showToast({ title: "Failed to disable user", variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnableUser = async (userId: string) => {
    setActionLoading(userId);
    try {
      await adminApi.put(`/admin/users/${userId}/enable`, {});
      showToast({ title: "User enabled", variant: "success" });
      fetchUsers(page);
    } catch (e) {
      showToast({ title: "Failed to enable user", variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  // ── Cooldown controls ──────────────────────────────────────
  // Admins can impose a 6h cooldown (or any duration) on a user, or
  // dismiss any active cooldown. The same endpoints also pick up
  // auto-rate-limit cooldowns; admins can manually override them.
  const handleImposeCooldown = async (userId: string) => {
    const note = window.prompt(
      "Cooldown note (optional, shown to admin):",
      "Manual cooldown imposed by admin",
    );
    if (note === null) return; // cancelled
    setActionLoading(userId);
    try {
      // Default to 6h (21600000ms) — the same as the auto rate-limit
      // cooldown. Admins can also pass a custom duration via the API
      // directly; this UI is a shortcut.
      await adminApi.put(`/admin/users/${userId}/cooldown`, {
        durationMs: 6 * 60 * 60 * 1000,
        note: note || null,
      });
      showToast({ title: "Cooldown imposed", description: "6h cooldown applied", variant: "success" });
      fetchUsers(page);
    } catch (e) {
      showToast({ title: "Failed to impose cooldown", variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismissCooldown = async (userId: string) => {
    setActionLoading(userId);
    try {
      await adminApi.delete(`/admin/users/${userId}/cooldown`);
      showToast({ title: "Cooldown dismissed", variant: "success" });
      fetchUsers(page);
    } catch (e) {
      showToast({ title: "Failed to dismiss cooldown", variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  // Returns true if the user is currently in cooldown (admin-or-auto).
  const inCooldown = (u: { cooldownUntil?: string | null }) => {
    if (!u.cooldownUntil) return false;
    return new Date(u.cooldownUntil).getTime() > Date.now();
  };

  const openRoleModal = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowRoleModal(true);
  };

  const handleRoleChange = async () => {
    if (!selectedUser) return;
    setActionLoading(selectedUser.id);
    try {
      await adminApi.put(`/admin/users/${selectedUser.id}/role`, { role: newRole });
      setShowRoleModal(false);
      setSelectedUser(null);
      showToast({ title: "Role updated", variant: "success" });
      fetchUsers(page);
    } catch (e) {
      showToast({ title: "Failed to update role", variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setActionLoading(selectedUser.id);
    try {
      await adminApi.delete(`/admin/users/${selectedUser.id}`);
      setShowDeleteModal(false);
      setSelectedUser(null);
      showToast({ title: "User deleted", variant: "success" });
      fetchUsers(page);
    } catch (e) {
      showToast({ title: "Failed to delete user", variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const tierOptions = TIER_OPTIONS.map((t) => ({
    value: t as TierFilter,
    label: t === "ALL" ? "All tiers" : t,
  }));

  const from = users.length === 0 ? 0 : (page - 1) * 25 + 1;
  const to = (page - 1) * 25 + users.length;

  return (
    <div>
      <PageHeader
        eyebrow="Users"
        title="Users"
        subtitle={`${total.toLocaleString()} registered accounts`}
        actions={
          <Button variant="primary">
            <UserPlus className="h-4 w-4" />
            Invite user
          </Button>
        }
      />

      {/* Toolbar */}
      <Card className="!p-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <SearchField
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by name or email…"
          />
          <Segmented<TierFilter>
            value={tier}
            options={tierOptions}
            onChange={setTier}
          />
          <div className="ml-auto flex items-center gap-2 text-[12px] text-[var(--color-tertiary-label)]">
            <Button variant="ghost" size="sm" onClick={() => fetchUsers(page)}>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
            <span>{total.toLocaleString()} total</span>
          </div>
        </div>
      </Card>

      {/* User list */}
      <Card className="!p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner className="!h-6 !w-6" />
          </div>
        ) : error ? (
          <div className="px-6 py-12 text-center">
            <p className="text-[14px] text-[var(--color-red)]">{error}</p>
            <Button variant="tertiary" size="sm" className="mt-3" onClick={() => fetchUsers(page)}>
              Try again
            </Button>
          </div>
        ) : users.length === 0 ? (
          <Empty
            icon={<UserCog className="h-5 w-5" />}
            title="No users found"
            message="Try a different search or filter."
          />
        ) : (
          <List className="!rounded-none">
            {users.map((u, i) => (
              <ListItem
                key={u.id}
                noSeparator={i === 0}
                leading={
                  <span className="text-[13px] font-semibold">
                    {(u.name || u.email).charAt(0).toUpperCase()}
                  </span>
                }
                leadingTint={TIER_LEADING_TINT[u.subscriptionTier] ?? "blue"}
                title={
                  <span className="flex items-center gap-2">
                    <span className="font-semibold">{u.name || "—"}</span>
                    {u.disabled && <Badge variant="error">Disabled</Badge>}
                    {u.role === "admin" && <Badge variant="warning">Admin</Badge>}
                  </span>
                }
                subtitle={
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {u.email}
                    </span>
                    <span>· {u._count?.proposals ?? 0} proposals</span>
                    <span>· Joined {new Date(u.createdAt).toLocaleDateString()}</span>
                    {inCooldown(u) && u.cooldownUntil && (
                      <span className="flex items-center gap-1 text-[var(--color-amber,#FFC25C)]">
                        <Clock className="h-3 w-3" />
                        In cooldown until {new Date(u.cooldownUntil).toLocaleString()}
                      </span>
                    )}
                  </span>
                }
                trailing={
                  <div className="flex items-center gap-2">
                    <Badge variant={TIER_VARIANT[u.subscriptionTier] ?? "neutral"}>
                      {u.subscriptionTier}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      className="cursor-pointer"
                      title="Cycle tier"
                      onClick={() => {
                        const next = u.subscriptionTier === "FREE" ? "PRO" : "FREE";
                        handleUpgradeTier(u.id, next);
                      }}
                    >
                      {u.subscriptionTier === "FREE" ? "→ PRO" : "→ FREE"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      className="cursor-pointer"
                      title="Change role"
                      aria-label="Change role"
                      onClick={() => openRoleModal(u)}
                    >
                      <UserCog className="h-3.5 w-3.5" />
                    </Button>
                    {u.disabled ? (
                      <Button
                        variant="outline"
                        size="sm"
                        iconOnly
                        className="cursor-pointer"
                        aria-label="Enable user"
                        disabled={actionLoading === u.id}
                        onClick={() => handleEnableUser(u.id)}
                      >
                        <CheckCircle className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        iconOnly
                        className="cursor-pointer"
                        aria-label="Disable user"
                        disabled={actionLoading === u.id}
                        onClick={() => handleDisableUser(u.id)}
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {/* Cooldown controls */}
                    {inCooldown(u) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        iconOnly
                        className="cursor-pointer text-[var(--color-amber,#FFC25C)]"
                        title={`Dismiss cooldown${u.cooldownNote ? ` — ${u.cooldownNote}` : ""}`}
                        aria-label="Dismiss cooldown"
                        disabled={actionLoading === u.id}
                        onClick={() => handleDismissCooldown(u.id)}
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        iconOnly
                        className="cursor-pointer"
                        title="Impose 6h cooldown"
                        aria-label="Impose cooldown"
                        disabled={actionLoading === u.id}
                        onClick={() => handleImposeCooldown(u.id)}
                      >
                        <Shield className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      className="cursor-pointer"
                      title="Delete"
                      aria-label="Delete user"
                      disabled={actionLoading === u.id}
                      onClick={() => openDeleteModal(u)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-[var(--color-red)]" />
                    </Button>
                  </div>
                }
              />
            ))}
          </List>
        )}
      </Card>

      {/* Pagination */}
      {users.length > 0 && (
        <div className="mt-4 flex items-center justify-between text-[12px] text-[var(--color-tertiary-label)]">
          <span>
            {from}–{to} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Role change dialog */}
      <Dialog
        open={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setSelectedUser(null);
        }}
        title="Change role"
        message={`Set a new role for ${selectedUser?.name || selectedUser?.email}.`}
      >
        <div className="mt-4">
          <Select
            label="Role"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            options={[
              { value: "user", label: "User" },
              { value: "admin", label: "Admin" },
              { value: "moderator", label: "Moderator" },
            ]}
          />
        </div>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRoleChange}
            disabled={actionLoading === selectedUser?.id}
          >
            Save
          </Button>
        </div>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteUser}
        title="Delete user?"
        description={`This will permanently delete ${selectedUser?.name || selectedUser?.email} and all their proposals. This action cannot be undone.`}
        confirmLabel={actionLoading === selectedUser?.id ? "Deleting…" : "Delete"}
        loading={actionLoading === selectedUser?.id}
      />
    </div>
  );
}